import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

import {
  Button,
  PencilIcon,
  PlusIcon,
  Tooltip,
  TrashIcon,
} from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'

import type { ChangeEvent } from 'react'

type AvatarPickerProps = {
  readonly onChange: (event: File | undefined) => void
  readonly onDelete?: () => void
  readonly acceptableExtensions: ReadonlyArray<string>
  readonly errorMessage?: string
  readonly required?: boolean
  readonly preview?: string | undefined
  readonly disabled?: boolean | undefined
  readonly tooltipContent?: React.ReactNode
  readonly placeholder?: React.ReactNode
}

export const AvatarPicker = (props: AvatarPickerProps) => {
  const { t } = useTranslation()
  const [preview, setPreview] = useState<string | undefined>(
    props.preview || undefined,
  )
  const inputRef = useRef<HTMLInputElement>(null)
  const objectUrlRef = useRef<string | undefined>(undefined)

  const revokeObjectUrl = () => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = undefined
    }
  }

  useEffect(() => () => revokeObjectUrl(), [])

  const setObjectUrlPreview = (url: string) => {
    revokeObjectUrl()
    objectUrlRef.current = url
    setPreview(url)
  }

  const clearPreview = () => {
    revokeObjectUrl()
    setPreview(undefined)
  }

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setObjectUrlPreview(URL.createObjectURL(file))
    }
    props.onChange(file)
  }

  return (
    <Root>
      <LabelInput
        $disabled={props.disabled ?? false}
        $hasPreview={!!preview}
        $hasError={!!props.errorMessage}
      >
        <VisuallyHidden.Root>
          <input
            disabled={props.disabled ?? false}
            type="file"
            accept={props.acceptableExtensions
              .map((x) => `image/${x}`)
              .join(', ')}
            ref={inputRef}
            onChange={onChange}
          />
        </VisuallyHidden.Root>
        {preview ? (
          <Preview $preview={preview} />
        ) : (
          (props.placeholder ?? (
            <div className={'placeholder'}>
              <PlusIcon width={16} height={16} />
              <div>
                {t('common.add')}
                {props.required ? <Required>*</Required> : undefined}
              </div>
            </div>
          ))
        )}
      </LabelInput>
      {(props.onDelete && props.placeholder) || preview ? (
        <ButtonStyled
          radius="full"
          size="h28"
          iconLeft={<TrashIcon />}
          onClick={() => {
            if (inputRef.current) {
              clearPreview()
              inputRef.current.value = ''
            }
            props.onDelete?.()
          }}
        />
      ) : undefined}
      <Tooltip content={props.tooltipContent}>
        <ButtonStyled
          onClick={() => {
            if (props.disabled) {
              return
            }
            inputRef.current?.click()
          }}
          radius="full"
          size="h28"
          style={{ right: 0, left: 'auto' }}
          iconLeft={<PencilIcon />}
        />
      </Tooltip>
    </Root>
  )
}

const Root = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const LabelInput = styled.label<{
  $hasPreview: boolean
  $hasError: boolean
  $disabled: boolean
}>`
  color: var(--text-10);
  position: relative;
  font-size: 11px;
  display: grid;
  gap: 8px;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  cursor: ${(_) => (_.$disabled ? 'auto' : 'pointer')};
  border-style: ${(_) => (_.$hasPreview ? 'solid' : 'dashed')};
  border-color: ${(_) => {
    if (_.$hasError) {
      return 'var(--errorA-6)'
    }

    return _.$hasPreview ? 'var(--background-2)' : 'var(--backgroundA-4)'
  }};
  border-width: 1px;

  .placeholder {
    position: absolute;
    top: 50%;
    left: 50%;
    margin: -50%;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: var(--background-2);
    padding: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`
const Preview = styled.div<{ $preview: string }>`
  background: ${({ $preview }) =>
    $preview ? `var(--background-2) url(${$preview})` : 'transparent'};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;

  position: absolute;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  margin: -50%;
  width: 100%;
  height: 100%;
`

const Required = styled.span`
  color: var(--error-9);
  font-weight: 600;
  font-size: 14px;
`

const ButtonStyled = styled(Button)`
  position: absolute;
  bottom: 0%;
  left: 0%;
  background: var(--background-2);
  color: var(--text-12);

  &:not(:disabled):hover {
    background: var(--background-3);
  }
`
